import EChartsReact from 'echarts-for-react';
import { useMemo } from 'react';
import { Typography } from '@analytical-alley/ui';

import { GREEN, RED } from './constants';

const ERROR_BAR_MAX = 20;

type PredictionErrorChartProps = {
    weeklyAccuracy: number | null | undefined;
    monthlyAccuracy: number | null | undefined;
};

const toError = (accuracy: number | null | undefined) =>
    accuracy != null ? Math.max(100 - accuracy, 0) : null;

export const PredictionErrorChart = ({
    weeklyAccuracy,
    monthlyAccuracy,
}: PredictionErrorChartProps) => {
    const items = useMemo(
        () =>
            [
                { label: 'Monthly', error: toError(monthlyAccuracy) },
                { label: 'Weekly', error: toError(weeklyAccuracy) },
            ].filter(
                (entry): entry is { label: string; error: number } =>
                    entry.error != null,
            ),
        [weeklyAccuracy, monthlyAccuracy],
    );

    const option = useMemo(
        () => ({
            grid: {
                top: 4,
                bottom: 18,
                left: 70,
                right: 16,
                containLabel: false,
            },
            xAxis: {
                type: 'value',
                min: 0,
                max: ERROR_BAR_MAX,
                axisLabel: {
                    color: 'rgba(255,255,255,0.5)',
                    fontSize: 10,
                    formatter: '{value}%',
                },
                axisLine: { show: false },
                axisTick: { show: false },
                splitLine: {
                    lineStyle: { color: 'rgba(255,255,255,0.08)' },
                },
            },
            yAxis: {
                type: 'category',
                data: items.map((d) => d.label),
                axisLabel: {
                    color: 'rgba(255,255,255,0.8)',
                    fontSize: 11,
                },
                axisLine: { show: false },
                axisTick: { show: false },
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                valueFormatter: (v: number) => `${v.toFixed(2)}%`,
            },
            series: [
                {
                    type: 'bar',
                    data: items.map((d) => Math.min(d.error, ERROR_BAR_MAX)),
                    barWidth: 12,
                    itemStyle: {
                        borderRadius: [0, 4, 4, 0],
                        color: GREEN,
                    },
                    label: {
                        show: true,
                        position: 'right',
                        color: 'rgba(255,255,255,0.9)',
                        fontSize: 11,
                        formatter: (params: { dataIndex: number }) => {
                            const value = items[params.dataIndex]?.error;
                            return value != null ? `${value.toFixed(2)}%` : '';
                        },
                    },
                },
            ],
        }),
        [items],
    );

    if (items.length === 0) {
        return null;
    }

    return (
        <div className="flex flex-col gap-1">
            <Typography
                variant="bodyS"
                tooltip="Shows the average gap between the KPI predicted by the model and the actual business results."
            >
                Prediction Error
            </Typography>
            <EChartsReact
                option={option}
                notMerge
                style={{ height: items.length * 32 + 24, width: '100%' }}
                opts={{ renderer: 'svg' }}
            />
        </div>
    );
};
